import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getEmailDetails } from '../../Redux/Action/EmailHistoryAction';
import { clearSelectedEmail } from '../../Redux/Slices/EmailHistorySlice';
import EventHistoryModal from './EventHistoryModal';

const EmailViewModal = ({ show, emailId, onClose }) => {
  const dispatch = useDispatch();
  const { selectedEmail, selectedEmailLoading, selectedEmailError } = useSelector(
    (state) => state.emailHistory
  );
  const [showEventHistory, setShowEventHistory] = useState(false);

  useEffect(() => {
    if (show && emailId) {
      dispatch(getEmailDetails(emailId));
    }
    
    return () => {
      if (!show) {
        dispatch(clearSelectedEmail());
      }
    };
  }, [dispatch, show, emailId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { color: '#6c757d', bg: '#e2e3e5', text: 'Sent', icon: 'fas fa-paper-plane' },
      delivered: { color: '#28a745', bg: '#d4edda', text: 'Delivered', icon: 'fas fa-check-circle' },
      bounced: { color: '#dc3545', bg: '#f8d7da', text: 'Bounced', icon: 'fas fa-exclamation-triangle' },
      failed: { color: '#dc3545', bg: '#f8d7da', text: 'Failed', icon: 'fas fa-times-circle' },
      pending: { color: '#ffc107', bg: '#fff3cd', text: 'Pending', icon: 'fas fa-clock' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        color: config.color,
        backgroundColor: config.bg,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: `1px solid ${config.color}20`
      }}>
        <i className={config.icon} style={{ fontSize: '10px' }}></i>
        {config.text}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      'OTP Verification': { bg: '#e3f2fd', color: '#1976d2', icon: 'fas fa-key' },
      'Booking Confirmation': { bg: '#e8f5e8', color: '#2e7d32', icon: 'fas fa-calendar-check' },
      'Booking Cancellation': { bg: '#ffebee', color: '#c62828', icon: 'fas fa-calendar-times' },
      'Password Reset': { bg: '#f3e5f5', color: '#7b1fa2', icon: 'fas fa-lock' },
      'Welcome': { bg: '#e0f2f1', color: '#00695c', icon: 'fas fa-hand-wave' },
      'Notification': { bg: '#fff3e0', color: '#ef6c00', icon: 'fas fa-bell' },
      'Custom': { bg: '#fce4ec', color: '#ad1457', icon: 'fas fa-edit' },
      'System': { bg: '#f5f5f5', color: '#424242', icon: 'fas fa-cog' },
      'Transactional': { bg: '#e8eaf6', color: '#3f51b5', icon: 'fas fa-receipt' }
    };

    const config = categoryColors[category] || categoryColors['System'];

    return (
      <span style={{
        padding: '6px 12px',
        borderRadius: '16px',
        fontSize: '12px',
        fontWeight: '600',
        color: config.color,
        backgroundColor: config.bg,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: `1px solid ${config.color}20`
      }}>
        <i className={config.icon} style={{ fontSize: '10px' }}></i>
        {category}
      </span>
    );
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        border: '1px solid #e9ecef',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '24px 28px',
          borderBottom: '1px solid #f1f3f4',
          background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-envelope-open-text" style={{ fontSize: '18px' }}></i>
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>Email Details</h3>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>View complete email information and delivery status</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '28px', backgroundColor: '#fafbfc' }}>
          {selectedEmailLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 40px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ 
                display: 'inline-block', 
                width: '48px', 
                height: '48px', 
                border: '4px solid #f3f3f4', 
                borderTop: '4px solid #007bff', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite' 
              }}></div>
              <p style={{ marginTop: '16px', color: '#6c757d', fontSize: '16px', fontWeight: '500' }}>Loading email details...</p>
            </div>
          ) : selectedEmailError ? (
            <div style={{ 
              padding: '24px', 
              backgroundColor: '#fff5f5', 
              color: '#c53030', 
              borderRadius: '12px',
              border: '1px solid #fed7d7',
              textAlign: 'center'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '12px' }}></i>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>Error loading email: {selectedEmailError}</p>
            </div>
          ) : selectedEmail ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Email Header Card */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '24px', 
                borderRadius: '12px', 
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '20px', 
                      fontWeight: '600', 
                      color: '#212529',
                      lineHeight: '1.3'
                    }}>
                      {selectedEmail.email.subject}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      {getCategoryBadge(selectedEmail.email.category)}
                      {getStatusBadge(selectedEmail.delivery.status)}
                    </div>
                  </div>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                  gap: '20px'
                }}>
                  {/* Recipient Info */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <i className="fas fa-user" style={{ color: '#007bff', fontSize: '14px' }}></i>
                      <strong style={{ fontSize: '14px', color: '#495057' }}>Recipient</strong>
                    </div>
                    {selectedEmail.recipient.name && (
                      <div style={{ fontSize: '15px', fontWeight: '500', color: '#212529', marginBottom: '4px' }}>
                        {selectedEmail.recipient.name}
                      </div>
                    )}
                    <div style={{ fontSize: '13px', color: '#6c757d', fontFamily: 'monospace' }}>
                      {selectedEmail.recipient.email}
                    </div>
                  </div>

                  {/* Sender Info */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <i className="fas fa-paper-plane" style={{ color: '#28a745', fontSize: '14px' }}></i>
                      <strong style={{ fontSize: '14px', color: '#495057' }}>Sender</strong>
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '500', color: '#212529', marginBottom: '4px' }}>
                      {selectedEmail.sender.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6c757d' }}>
                      {selectedEmail.sender.system ? 'System Generated' : 'Admin Sent'} • no-reply@b4t.com
                    </div>
                  </div>

                  {/* Timing Info */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <i className="fas fa-clock" style={{ color: '#ffc107', fontSize: '14px' }}></i>
                      <strong style={{ fontSize: '14px', color: '#495057' }}>Timing</strong>
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '500', color: '#212529', marginBottom: '4px' }}>
                      {selectedEmail.delivery.sentAt 
                        ? formatDate(selectedEmail.delivery.sentAt) 
                        : 'Not sent yet'
                      }
                    </div>
                    {selectedEmail.delivery.deliveredAt && (
                      <div style={{ fontSize: '13px', color: '#28a745' }}>
                        <i className="fas fa-check" style={{ marginRight: '4px' }}></i>
                        Delivered {formatDate(selectedEmail.delivery.deliveredAt)}
                      </div>
                    )}
                  </div>
                </div>

                {selectedEmail.delivery.failureReason && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: '#fff5f5', 
                      color: '#c53030',
                      borderRadius: '8px',
                      border: '1px solid #fed7d7'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <i className="fas fa-exclamation-triangle" style={{ color: '#e53e3e' }}></i>
                        <strong>Failure Reason</strong>
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        {selectedEmail.delivery.failureReason}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Delivery Events */}
              {selectedEmail.delivery.events && selectedEmail.delivery.events.length > 0 && (
                <div style={{
                  backgroundColor: 'white',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: '#e3f2fd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fas fa-history" style={{ color: '#1976d2', fontSize: '14px' }}></i>
                      </div>
                      <h5 style={{ margin: 0, color: '#212529', fontSize: '18px', fontWeight: '600' }}>
                        Delivery Events
                      </h5>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {selectedEmail.delivery.events.length} event{selectedEmail.delivery.events.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowEventHistory(true)}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                      <i className="fas fa-external-link-alt"></i>
                      View Full Timeline
                    </button>
                  </div>
                  
                  <div style={{ 
                    backgroundColor: '#fafbfc',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '2fr 2fr 3fr', 
                      gap: '0',
                      backgroundColor: '#f8f9fa',
                      padding: '16px',
                      borderBottom: '1px solid #e9ecef',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#495057'
                    }}>
                      <div>Event Type</div>
                      <div>Timestamp</div>
                      <div>Event ID</div>
                    </div>
                    {selectedEmail.delivery.events.slice(0, 3).map((event, index) => (
                      <div 
                        key={index}
                        style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '2fr 2fr 3fr', 
                          gap: '0',
                          padding: '16px',
                          borderBottom: index < Math.min(selectedEmail.delivery.events.length, 3) - 1 ? '1px solid #f1f3f4' : 'none',
                          backgroundColor: 'white'
                        }}
                      >
                        <div>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: 'white',
                            backgroundColor: 
                              event.event === 'delivery' ? '#28a745' :
                              event.event === 'open' ? '#007bff' :
                              event.event === 'click' ? '#6f42c1' :
                              event.event === 'bounce' ? '#dc3545' :
                              event.event === 'spam' ? '#fd7e14' :
                              '#6c757d',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <i className={
                              event.event === 'delivery' ? 'fas fa-check' :
                              event.event === 'open' ? 'fas fa-envelope-open' :
                              event.event === 'click' ? 'fas fa-mouse-pointer' :
                              event.event === 'bounce' ? 'fas fa-exclamation-triangle' :
                              event.event === 'spam' ? 'fas fa-ban' :
                              'fas fa-info'
                            } style={{ fontSize: '9px' }}></i>
                            {event.event.charAt(0).toUpperCase() + event.event.slice(1)}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>
                          {formatDate(event.timestamp)}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          fontFamily: 'monospace', 
                          color: '#6c757d',
                          wordBreak: 'break-all'
                        }}>
                          {event.eventId}
                        </div>
                      </div>
                    ))}
                    {selectedEmail.delivery.events.length > 3 && (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '16px', 
                        backgroundColor: '#f8f9fa',
                        borderTop: '1px solid #e9ecef'
                      }}>
                        <span style={{ fontSize: '13px', color: '#6c757d', marginRight: '12px' }}>
                          Showing 3 of {selectedEmail.delivery.events.length} events
                        </span>
                        <button
                          onClick={() => setShowEventHistory(true)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          View All Events
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Email Content Preview */}
              <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: '#e8f5e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="fas fa-eye" style={{ color: '#2e7d32', fontSize: '14px' }}></i>
                  </div>
                  <h5 style={{ margin: 0, color: '#212529', fontSize: '18px', fontWeight: '600' }}>
                    Email Content Preview
                  </h5>
                </div>
                
                <div style={{ 
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  textAlign: 'center',
                  padding: '40px 24px',
                  border: '2px dashed #dee2e6'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    backgroundColor: '#007bff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px auto'
                  }}>
                    <i className="fas fa-external-link-alt" style={{ fontSize: '24px', color: 'white' }}></i>
                  </div>
                  <h6 style={{ 
                    margin: '0 0 8px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#212529'
                  }}>
                    Preview in New Window
                  </h6>
                  <p style={{ 
                    margin: '0 0 20px 0', 
                    fontSize: '14px',
                    color: '#6c757d',
                    lineHeight: '1.5'
                  }}>
                    Email preview opens in a separate window for accurate rendering without CSS conflicts.
                    This shows the email exactly as recipients see it.
                  </p>
                  <button
                    onClick={() => {
                      const newWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
                      newWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>Email Preview - ${selectedEmail.email.subject}</title>
                          <meta charset="utf-8">
                          <meta name="viewport" content="width=device-width, initial-scale=1.0">
                          <style>
                            body { 
                              margin: 20px; 
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                              background-color: #f5f5f5;
                              line-height: 1.6;
                            }
                            .email-container {
                              background-color: white;
                              padding: 30px;
                              border-radius: 12px;
                              box-shadow: 0 4px 20px rgba(0,0,0,0.1);
                              max-width: 600px;
                              margin: 0 auto;
                            }
                            .email-header {
                              background: linear-gradient(135deg, #059669 0%, #0d9488 100%);
                              color: white;
                              padding: 20px;
                              border-radius: 8px;
                              margin-bottom: 24px;
                            }
                            .email-header h3 {
                              margin: 0 0 8px 0;
                              font-size: 20px;
                              font-weight: 600;
                            }
                            .email-header p {
                              margin: 0;
                              opacity: 0.9;
                              font-size: 14px;
                            }
                            .email-content {
                              color: #333;
                              line-height: 1.6;
                            }
                          </style>
                        </head>
                        <body>
                          <div class="email-container">
                            <div class="email-header">
                              <h3>${selectedEmail.email.subject}</h3>
                              <p>From: ${selectedEmail.sender.name} &lt;no-reply@b4t.com&gt;</p>
                              <p>To: ${selectedEmail.recipient.email}</p>
                            </div>
                            <div class="email-content">
                              ${selectedEmail.email.htmlContent}
                            </div>
                          </div>
                        </body>
                        </html>
                      `);
                      newWindow.document.close();
                    }}
                    style={{
                      padding: '14px 28px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      margin: '0 auto',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,123,255,0.2)'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#0056b3';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 8px rgba(0,123,255,0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#007bff';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 4px rgba(0,123,255,0.2)';
                    }}
                  >
                    <i className="fas fa-external-link-alt"></i>
                    Open Email Preview
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 40px', 
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e9ecef'
            }}>
              <i className="fas fa-inbox" style={{ fontSize: '48px', color: '#dee2e6', marginBottom: '16px' }}></i>
              <h5 style={{ margin: '0 0 8px 0', color: '#495057', fontSize: '18px' }}>No Email Data</h5>
              <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>
                The requested email information is not available.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '20px 28px', 
          borderTop: '1px solid #f1f3f4',
          backgroundColor: '#fafbfc',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
          >
            <i className="fas fa-times"></i>
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Event History Modal */}
      <EventHistoryModal
        show={showEventHistory}
        events={selectedEmail?.delivery?.events || []}
        emailSubject={selectedEmail?.email?.subject || ''}
        onClose={() => setShowEventHistory(false)}
      />
    </div>
  );
};

export default EmailViewModal;